// WHAT: Hook for fetching and managing Yazio daily calories data
// WHY:  Provides interface for loading daily calorie information with caching
// DOMAIN: Health → Yazio
// DEPENDENCIES: core.invoke for backend commands
// RISKS:
//   - NOTE: Error handling is generic - may not provide detailed feedback
//   - NOTE: Auto-load on mount may cause unnecessary API calls
//   - NOTE: No refresh mechanism for stale data

import type { DailyCaloriesRequest, DailyCaloriesResponse } from "../types";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

/**
 * Daily calories state interface
 */
interface DailyCaloriesState {
  /** Calorie data */
  data: DailyCaloriesResponse | undefined;
  /** Error message */
  error: string | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Last update timestamp */
  lastUpdated: Date | undefined;
}

/**
 * WHAT:
 *   Hook that provides interface for fetching and managing Yazio daily calories
 *   data with automatic loading and refresh capabilities.
 *
 * WHY:
 *   Centralizes daily calorie data management and provides consistent interface
 *   for components.
 *
 * BEHAVIOR:
 *   - Auto-loads today's data on mount
 *   - Supports loading data for specific dates
 *   - Provides refresh functionality
 *   - Manages loading and error states
 *
 * RISKS:
 *   - NOTE: Error handling is generic - may not provide detailed feedback
 *   - NOTE: Auto-load on mount may cause unnecessary API calls
 *   - NOTE: No refresh mechanism for stale data
 *
 * @returns Object with calorie data state and management functions
 *
 */
export function useYazioDailyCalories() {
  const [state, setState] = useState<DailyCaloriesState>({
    data: undefined,
    error: undefined,
    isLoading: false,
    lastUpdated: undefined,
  });

  const fetchDailyCalories = useCallback(async (request: DailyCaloriesRequest = {}) => {
    try {
      setState((previous) => ({ ...previous, error: undefined, isLoading: true }));

      const data = await core.invoke<DailyCaloriesResponse>("get_yazio_daily_calories", {
        date: request.date,
      });

      setState((previous) => ({
        ...previous,
        data,
        isLoading: false,
        lastUpdated: new Date(),
      }));
    } catch {
      // NOTE: Failed to fetch daily calories
      setState((previous) => ({
        ...previous,
        error: "Failed to load daily calories data",
        isLoading: false,
      }));
    }
  }, []);

  /**
   * WHAT:
   *   Refreshes current data.
   *
   * WHY:
   *   Enables manual data refresh.
   *
   * @returns void
   *
   */
  const refreshData = () => {
    if (state.data) {
      void fetchDailyCalories({ date: state.data.date });
    } else {
      void fetchDailyCalories();
    }
  };

  /**
   * WHAT:
   *   Loads today's calorie data.
   *
   * WHY:
   *   Convenience function for loading current day.
   *
   * @returns void
   *
   */
  const loadTodayData = useCallback(() => {
    void fetchDailyCalories();
  }, [fetchDailyCalories]);

  /**
   * WHAT:
   *   Loads calorie data for specific date.
   *
   * WHY:
   *   Enables historical data viewing.
   *
   * @param date - Date string to load data for
   *
   * @returns void
   *
   */
  const loadDateData = (date: string) => {
    void fetchDailyCalories({ date });
  };

  /**
   * WHAT:
   *   Clears error state.
   *
   * WHY:
   *   Allows user to dismiss errors.
   *
   * @returns void
   *
   */
  const clearError = () => {
    setState((previous) => ({ ...previous, error: undefined }));
  };

  // NOTE: Auto-load today's data on mount
  useEffect(() => {
    loadTodayData();
  }, [loadTodayData]);

  return {
    ...state,
    clearError,
    fetchDailyCalories,
    loadDateData,
    loadTodayData,
    refreshData,
  };
}
